/**
 * src/pages/ClienteDetalle.jsx
 * Vista detalle de un cliente: info, empresas propias, competidores, scheduler.
 */
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Header } from '../components/layout/Header'
import { Card } from '../components/ui/Card'
import { Badge, TIER_COLOR } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input, Toggle, Select } from '../components/ui/Input'
import { Table } from '../components/ui/Table'
import {
  useCliente, useUso, useUpdateCompetidor, useUpdateEmpresa, useUpdateScheduler, useAddCompetidor,
  useDeleteCliente, useUpdateTierOverride, useUpdateEstado, useCancelarSuscripcion,
} from '../hooks/useClientes'

const TIER_OPTS = [
  { value: 'basic', label: 'Basic' },
  { value: 'pro', label: 'Pro' },
  { value: 'premium', label: 'Premium' },
  { value: 'enterprise', label: 'Enterprise' },
]
const _fmtFecha = (iso) => iso ? new Date(iso).toLocaleDateString('es-CL') : null
// Valor por defecto seguro para la baja programada (fin del mes en curso), sin
// depender de la suscripción. El admin lo ajusta; sirve para que el campo nunca
// quede en blanco cuando se activa "Programar baja".
const _finDeMes = () => {
  const d = new Date()
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  const mm = String(last.getMonth() + 1).padStart(2, '0')
  const dd = String(last.getDate()).padStart(2, '0')
  return `${last.getFullYear()}-${mm}-${dd}`
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000'
import api from '../api/client'

const FREQ_OPTS = [
  { value: '24h', label: 'Cada 24h' },
  { value: '48h', label: 'Cada 48h' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'manual', label: 'Manual' },
]

function TabBtn({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 16px', fontSize: '13px', fontWeight: active ? 500 : 400,
        color: active ? 'var(--color-accent)' : 'var(--color-text-3)',
        background: 'none', border: 'none', cursor: 'pointer',
        borderBottom: active ? '2px solid var(--color-accent)' : '2px solid transparent',
        transition: 'all 0.15s', fontFamily: 'var(--font-ui)',
      }}
    >
      {label}
    </button>
  )
}

function InfoTab({ cliente }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
      {[
        ['ID', cliente.cliente_id],
        ['Nombre', cliente.nombre],
        ['Email', cliente.email || '—'],
        ['Tier', <Badge color={TIER_COLOR[cliente.tier] || 'gray'}>{cliente.tier}</Badge>],
        ['Familia CM', cliente.familia_cm],
        ['Estado', <Badge color={cliente.activo ? 'green' : 'gray'} dot>{cliente.activo ? 'Activo' : 'Inactivo'}</Badge>],
      ].map(([label, value]) => (
        <div key={label} style={{
          padding: '12px 14px', background: 'var(--color-surface-2)',
          border: '1px solid var(--color-border)', borderRadius: '8px',
        }}>
          <div style={{ fontSize: '10px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '5px' }}>
            {label}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--color-text)' }}>{value}</div>
        </div>
      ))}
      <div style={{ gridColumn: '1/-1', padding: '12px 14px', background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
        <div style={{ fontSize: '10px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '5px' }}>URL BASE</div>
        <div style={{ fontSize: '12px', color: 'var(--color-text)', fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{cliente.url_base}</div>
      </div>
    </div>
  )
}

function EmpresasTab({ empresas, clienteId }) {
  const updateMutation = useUpdateEmpresa(clienteId)
  const [editando, setEditando] = useState(null)   // empresa en edición | null
  const [form, setForm] = useState({ rut: '', nombre: '', seller_code: '' })
  const [verificando, setVerificando] = useState(false)
  const [hallazgo, setHallazgo] = useState(null)   // {empresa, seller_code} | {error}
  const [saveError, setSaveError] = useState('')
  const [savedId, setSavedId] = useState(null)

  function abrirEdicion(emp) {
    setEditando(emp)
    setForm({ rut: emp.rut ?? '', nombre: emp.nombre ?? '', seller_code: emp.seller_code ?? '' })
    setHallazgo(null)
    setSaveError('')
  }

  function cerrarEdicion() {
    setEditando(null)
    setHallazgo(null)
    setSaveError('')
  }

  // Verifica el RUT contra Mercado Público y autocompleta nombre + seller_code
  // (mismo endpoint que usa el wizard de onboarding).
  async function verificarRut() {
    const rut = form.rut.trim()
    if (!rut) { setHallazgo({ error: 'Escribe el RUT primero.' }); return }
    setVerificando(true); setHallazgo(null)
    try {
      const { data } = await api.get('/api/diagnostico/confirmar', { params: { rut } })
      if (data?.encontrada && data?.seller_code) {
        setForm(f => ({
          ...f,
          rut:         data.rut || f.rut,
          nombre:      data.empresa || f.nombre,
          seller_code: String(data.seller_code),
        }))
        setHallazgo({ empresa: data.empresa, seller_code: data.seller_code })
      } else {
        setHallazgo({ error: 'No encontramos esa empresa como proveedor del Estado. Revisa el RUT.' })
      }
    } catch (err) {
      setHallazgo({ error: 'No se pudo verificar el RUT ahora. Inténtalo de nuevo.' })
    } finally { setVerificando(false) }
  }

  async function guardar() {
    setSaveError('')
    const data = {
      rut:         form.rut.trim(),
      nombre:      form.nombre.trim(),
      seller_code: form.seller_code.trim(),
    }
    if (!data.rut || !data.nombre || !data.seller_code) {
      setSaveError('RUT, nombre y seller code son obligatorios.')
      return
    }
    try {
      const empId = editando.id
      await updateMutation.mutateAsync({ empId, data })
      cerrarEdicion()
      setSavedId(empId)
      setTimeout(() => setSavedId(null), 2500)
    } catch (err) {
      setSaveError(err?.response?.data?.detail || 'No se pudo guardar. Inténtalo de nuevo.')
    }
  }

  const COLS = [
    { key: 'rut', label: 'RUT', mono: true },
    { key: 'nombre', label: 'Nombre' },
    { key: 'seller_code', label: 'Seller Code', mono: true },
    {
      key: 'acciones', label: '', width: '90px', align: 'right',
      render: (_, row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          {savedId === row.id && (
            <span style={{ fontSize: '11px', color: 'var(--color-ok)', fontFamily: 'var(--font-mono)' }}>✓</span>
          )}
          <Button
            size="sm" variant="secondary"
            icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>}
            onClick={() => abrirEdicion(row)}
          >
            Editar
          </Button>
        </span>
      ),
    },
  ]

  return (
    <div>
      <Table columns={COLS} data={empresas} emptyMessage="Sin empresas propias" />

      {editando && (
        <div style={{
          marginTop: '16px', padding: '16px',
          background: 'var(--color-surface-2)',
          border: '1px solid var(--color-border)', borderRadius: '8px',
        }}>
          <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '14px' }}>
            EDITAR EMPRESA · #{editando.id}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', alignItems: 'end' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <Input
                label="RUT"
                value={form.rut}
                onChange={e => setForm(f => ({ ...f, rut: e.target.value }))}
                placeholder="76.000.000-0"
                style={{ flex: 1 }}
              />
              <Button size="md" variant="secondary" onClick={verificarRut} loading={verificando}>
                Verificar
              </Button>
            </div>
            <Input
              label="NOMBRE"
              value={form.nombre}
              onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
              placeholder="Razón social"
            />
            <Input
              label="SELLER CODE"
              value={form.seller_code}
              onChange={e => setForm(f => ({ ...f, seller_code: e.target.value }))}
              placeholder="Código proveedor MP"
            />
          </div>

          {hallazgo?.empresa && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--color-ok)' }}>
              ✓ {hallazgo.empresa} · código {hallazgo.seller_code}
            </div>
          )}
          {hallazgo?.error && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--color-error)' }}>
              {hallazgo.error}
            </div>
          )}
          {saveError && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--color-error)' }}>
              {saveError}
            </div>
          )}

          <div style={{ marginTop: '14px', display: 'flex', gap: '8px' }}>
            <Button onClick={guardar} loading={updateMutation.isPending}>
              Guardar cambios
            </Button>
            <Button variant="ghost" onClick={cerrarEdicion} disabled={updateMutation.isPending}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function CompetidoresTab({ competidores, clienteId }) {
  const updateMutation = useUpdateCompetidor(clienteId)

  const COLS = [
    { key: 'rut', label: 'RUT', mono: true },
    { key: 'nombre', label: 'Nombre' },
    { key: 'seller_code', label: 'Seller Code', mono: true },
    {
      key: 'activo', label: 'Activo', width: '70px', align: 'center',
      render: (v, row) => (
        <Toggle
          checked={v}
          onChange={(val) => updateMutation.mutate({ compId: row.id, data: { activo: val } })}
        />
      ),
    },
  ]
  return <Table columns={COLS} data={competidores} emptyMessage="Sin competidores configurados" />
}

function SchedulerTab({ scheduler, clienteId }) {
  const updateMutation = useUpdateScheduler(clienteId)
  const [local, setLocal] = useState({
    activo_precios: scheduler?.activo_precios ?? false,
    freq_precios: scheduler?.freq_precios ?? '48h',
    activo_ocs: scheduler?.activo_ocs ?? false,
    freq_ocs: scheduler?.freq_ocs ?? 'semanal',
  })
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)

  async function handleSave() {
    setError(null)
    try {
      await updateMutation.mutateAsync(local)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al guardar la configuración')
    }
  }

  function Row({ label, activeKey, freqKey }) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 0', borderBottom: '1px solid var(--color-border)',
      }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text)' }}>{label}</div>
          <div style={{ fontSize: '11px', color: 'var(--color-text-3)', marginTop: '2px' }}>
            {local[activeKey] ? `Activo · ${local[freqKey]}` : 'Inactivo'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Select
            value={local[freqKey]}
            onChange={e => setLocal(l => ({ ...l, [freqKey]: e.target.value }))}
            options={FREQ_OPTS}
            style={{ width: '130px' }}
          />
          <Toggle
            checked={local[activeKey]}
            onChange={val => setLocal(l => ({ ...l, [activeKey]: val }))}
          />
        </div>
      </div>
    )
  }

  return (
    <div>
      <Row label="Scraping de precios" activeKey="activo_precios" freqKey="freq_precios" />
      <Row label="Scraping de OCs" activeKey="activo_ocs" freqKey="freq_ocs" />
      <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Button onClick={handleSave} loading={updateMutation.isPending}>
          Guardar configuración
        </Button>
        {saved && (
          <span style={{ fontSize: '12px', color: 'var(--color-ok)', fontFamily: 'var(--font-mono)' }}>
            ✓ Guardado
          </span>
        )}
        {error && (
          <span style={{ fontSize: '12px', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>
            {error}
          </span>
        )}
      </div>
    </div>
  )
}

// ── Uso / Plan (companion admin del indicador de uso) ─────────────────────────

const _fmt = (n) => (n ?? 0).toLocaleString('es-CL')

function UsoBar({ label, m, extra }) {
  // m = {n, lim, over, mon?}. lim=0 → ilimitado.
  if (!m) return null
  const ilim = m.lim === 0
  const pct = ilim ? 100 : Math.min(100, Math.round((m.n / Math.max(1, m.lim)) * 100))
  const col = m.over ? '#ef4444' : (ilim ? '#10b981' : 'var(--color-accent)')
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '5px' }}>
        <span style={{ color: 'var(--color-text)' }}>{label}</span>
        <span style={{ fontWeight: 600, color: col }}>
          {ilim ? `${_fmt(m.n)} · ilimitado` : `${_fmt(m.n)} / ${_fmt(m.lim)}`}{m.over ? ' ⚠' : ''}
        </span>
      </div>
      <div style={{ height: '6px', background: 'var(--color-surface-2)', borderRadius: '999px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: col, borderRadius: '999px' }} />
      </div>
      {extra && <div style={{ fontSize: '11px', color: 'var(--color-text-3)', marginTop: '4px' }}>{extra}</div>}
    </div>
  )
}

function UsoTab({ clienteId }) {
  const { data, isLoading, error } = useUso(clienteId)
  const muted = { color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: '12px', padding: '8px 0' }
  if (isLoading) return <div style={muted}>Cargando uso…</div>
  if (error) return <div style={{ ...muted, color: 'var(--color-error)' }}>No se pudo cargar el uso.</div>

  const uso = data?.uso || {}
  if (!uso.tier) return (
    <div style={muted}>
      Aún no hay snapshot de uso. Se genera en el primer run del pipeline del cliente
      (regenerar dashboard).
    </div>
  )

  const f = uso.familias, p = uso.productos
  const anyOver = ['familias', 'productos', 'competidores', 'empresas'].some(k => uso[k]?.over)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600 }}>
          Plan <Badge color={TIER_COLOR[uso.tier] || 'gray'}>{uso.tier}</Badge>
        </div>
        {anyOver && uso.siguiente && (
          <Badge color="yellow">Oportunidad de upsell → {uso.siguiente}</Badge>
        )}
      </div>

      <UsoBar label="Familias monitorizadas" m={f}
        extra={f && f.mon != null && f.mon !== f.n ? `Monitorea ${_fmt(f.mon)} de ${_fmt(f.n)} familias detectadas` : null} />
      <UsoBar label="Productos propios" m={p}
        extra={p && p.mon != null && p.mon !== p.n ? `Monitorea ${_fmt(p.mon)} de ${_fmt(p.n)} productos del catálogo` : null} />
      <UsoBar label="Competidores" m={uso.competidores} />
      <UsoBar label="Empresas propias" m={uso.empresas} />

      <div style={{ fontSize: '11px', color: 'var(--color-text-3)', marginTop: '10px', lineHeight: 1.5 }}>
        <span style={{ color: 'var(--color-error)' }}>⚠</span> = por encima del tope del plan (señal de upsell).
        {data?.actualizado_en && <> · Actualizado: {new Date(data.actualizado_en).toLocaleString('es-CL')}</>}
      </div>
    </div>
  )
}

function PlanTab({ cliente, clienteId }) {
  const overrideMut = useUpdateTierOverride(clienteId)
  const estadoMut = useUpdateEstado(clienteId)
  const cancelarMut = useCancelarSuscripcion(clienteId)
  const [msgSusc, setMsgSusc] = useState(null)

  async function cancelarSusc() {
    if (!window.confirm('¿Cancelar la renovación de esta suscripción? Se detiene el cobro en Flow y el servicio sigue hasta el fin del periodo pagado.')) return
    setMsgSusc(null)
    try {
      await cancelarMut.mutateAsync()
      setMsgSusc({ ok: true, text: 'Suscripción cancelada. Baja programada al fin del periodo.' })
    } catch (e) {
      setMsgSusc({ ok: false, text: e?.response?.data?.detail || 'No se pudo cancelar.' })
    }
  }

  const hayOverride = !!cliente.tier_override && cliente.tier_efectivo !== cliente.tier

  // ── Concesión de tier ──
  const [tier, setTier] = useState(cliente.tier_override || 'premium')
  const [modo, setModo] = useState('indefinido')   // indefinido | dias | fecha
  const [dias, setDias] = useState(30)
  const [hasta, setHasta] = useState('')
  const [motivo, setMotivo] = useState(cliente.tier_override_motivo || '')
  const [msg, setMsg] = useState(null)             // {ok, text}

  async function conceder() {
    setMsg(null)
    const payload = { tier, motivo: motivo.trim() || null }
    if (modo === 'dias') payload.dias = Number(dias)
    if (modo === 'fecha') {
      if (!hasta) { setMsg({ ok: false, text: 'Elige una fecha de vencimiento.' }); return }
      payload.hasta = hasta
    }
    try {
      await overrideMut.mutateAsync(payload)
      setMsg({ ok: true, text: 'Concesión aplicada.' })
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'No se pudo aplicar.' })
    }
  }

  async function quitar() {
    setMsg(null)
    try {
      await overrideMut.mutateAsync({ tier: null })
      setMsg({ ok: true, text: 'Concesión retirada.' })
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'No se pudo retirar.' })
    }
  }

  // ── Estado / baja ──
  const [activo, setActivo] = useState(cliente.activo)
  const [programarBaja, setProgramarBaja] = useState(!!cliente.baja_programada_en)
  const [baja, setBaja] = useState(cliente.baja_programada_en ? cliente.baja_programada_en.slice(0, 10) : '')
  const [msgEstado, setMsgEstado] = useState(null)

  // Fecha por defecto de la baja: el fin del periodo ya pagado (Flow) si se conoce;
  // si no, el fin del mes en curso. El admin puede ajustarla.
  const _bajaPorDefecto = () => (cliente.periodo_fin ? cliente.periodo_fin.slice(0, 10) : _finDeMes())

  // Red de seguridad: al activar "Programar baja" el campo NUNCA queda en blanco.
  function toggleProgramarBaja(on) {
    setProgramarBaja(on)
    if (on && !baja) setBaja(_bajaPorDefecto())
    setMsgEstado(null)
  }

  async function guardarEstado() {
    setMsgEstado(null)
    // Seguridad: si se pide programar baja, la fecha es obligatoria (no blanco).
    if (programarBaja && !baja) {
      setMsgEstado({ ok: false, text: 'Elige la fecha de baja o desactiva "Programar baja".' })
      return
    }
    try {
      await estadoMut.mutateAsync({ activo, baja_programada_en: programarBaja ? baja : null })
      setMsgEstado({ ok: true, text: 'Estado guardado.' })
    } catch (e) {
      setMsgEstado({ ok: false, text: e?.response?.data?.detail || 'No se pudo guardar.' })
    }
  }

  const box = {
    padding: '16px', background: 'var(--color-surface-2)',
    border: '1px solid var(--color-border)', borderRadius: '8px', marginBottom: '16px',
  }
  const titulo = {
    fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)',
    letterSpacing: '0.08em', marginBottom: '12px',
  }

  return (
    <div>
      {/* Resumen paga / disfruta */}
      <div style={box}>
        <div style={titulo}>PLAN ACTUAL</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', color: 'var(--color-text-2)' }}>
            Paga <Badge color={TIER_COLOR[cliente.tier] || 'gray'}>{cliente.tier}</Badge>
          </span>
          <span style={{ fontSize: '13px', color: 'var(--color-text-2)' }}>
            Disfruta <Badge color={TIER_COLOR[cliente.tier_efectivo] || 'gray'}>{cliente.tier_efectivo}</Badge>
          </span>
          {hayOverride && (
            <span style={{ fontSize: '12px', color: 'var(--color-text-3)' }}>
              · concesión {cliente.tier_override_hasta
                ? `hasta ${_fmtFecha(cliente.tier_override_hasta)}`
                : 'indefinida'}
              {cliente.tier_override_motivo ? ` (${cliente.tier_override_motivo})` : ''}
            </span>
          )}
        </div>
      </div>

      {/* Conceder tier */}
      <div style={box}>
        <div style={titulo}>CONCEDER TIER (SIN TOCAR LO QUE PAGA)</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', alignItems: 'end' }}>
          <Select label="TIER A CONCEDER" value={tier} onChange={e => setTier(e.target.value)} options={TIER_OPTS} />
          <Select label="VIGENCIA" value={modo} onChange={e => setModo(e.target.value)} options={[
            { value: 'indefinido', label: 'Indefinida (hasta nuevo cambio)' },
            { value: 'dias', label: 'Prueba de N días' },
            { value: 'fecha', label: 'Hasta una fecha' },
          ]} />
          {modo === 'dias' && (
            <Input label="DÍAS DE PRUEBA" type="number" value={dias} onChange={e => setDias(e.target.value)} />
          )}
          {modo === 'fecha' && (
            <Input label="VENCE EL" type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
          )}
          <Input label="MOTIVO (OPCIONAL)" value={motivo} onChange={e => setMotivo(e.target.value)}
                 placeholder="prueba comercial, cortesía…" />
        </div>
        <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Button onClick={conceder} loading={overrideMut.isPending}>Aplicar concesión</Button>
          {hayOverride && (
            <Button variant="ghost" onClick={quitar} loading={overrideMut.isPending}>Quitar concesión</Button>
          )}
          {msg && (
            <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)',
              color: msg.ok ? 'var(--color-ok)' : 'var(--color-error)' }}>
              {msg.ok ? '✓' : '✗'} {msg.text}
            </span>
          )}
        </div>
      </div>

      {/* Estado / baja */}
      <div style={box}>
        <div style={titulo}>ESTADO DEL SERVICIO</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '12px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text)' }}>Cliente activo</div>
            <div style={{ fontSize: '11px', color: 'var(--color-text-3)', marginTop: '2px' }}>
              {activo ? 'Recibe scraping y accede a su dashboard' : 'Servicio detenido (dashboard congelado sigue accesible)'}
            </div>
          </div>
          <Toggle checked={activo} onChange={setActivo} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: '12px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text)' }}>Programar baja</div>
            <div style={{ fontSize: '11px', color: 'var(--color-text-3)', marginTop: '2px' }}>
              {programarBaja ? 'El servicio se desactivará solo en la fecha indicada' : 'Sin baja programada — el servicio continúa indefinidamente'}
            </div>
          </div>
          <Toggle checked={programarBaja} onChange={toggleProgramarBaja} />
        </div>
        {programarBaja && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', alignItems: 'end', paddingTop: '12px' }}>
            <Input label="FECHA DE BAJA (VENCIMIENTO)" type="date" value={baja}
                   onChange={e => setBaja(e.target.value)}
                   error={!baja ? 'Obligatoria' : ''} />
            <div style={{ fontSize: '11px', color: 'var(--color-text-3)', paddingBottom: '9px' }}>
              Por defecto, el fin del mes en curso (p. ej. al agotarse el periodo ya pagado). Ajústala si procede.
            </div>
          </div>
        )}
        <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Button onClick={guardarEstado} loading={estadoMut.isPending}>Guardar estado</Button>
          {msgEstado && (
            <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)',
              color: msgEstado.ok ? 'var(--color-ok)' : 'var(--color-error)' }}>
              {msgEstado.ok ? '✓' : '✗'} {msgEstado.text}
            </span>
          )}
        </div>
      </div>

      {/* Suscripción (Flow) */}
      <div style={box}>
        <div style={titulo}>SUSCRIPCIÓN (FLOW)</div>
        {cliente.renovar_automaticamente == null ? (
          <div style={{ fontSize: '12px', color: 'var(--color-text-3)' }}>
            Este cliente no tiene una suscripción de pago vinculada (alta manual o sin Flow).
          </div>
        ) : cliente.renovar_automaticamente ? (
          <>
            <div style={{ fontSize: '13px', color: 'var(--color-text)', marginBottom: '4px' }}>
              Se renueva automáticamente{cliente.periodo_fin ? ` · próximo cargo hacia ${_fmtFecha(cliente.periodo_fin)}` : ''}.
            </div>
            <div style={{ fontSize: '11px', color: 'var(--color-text-3)', marginBottom: '12px' }}>
              Cancelar detiene el cobro en Flow; el servicio sigue hasta el fin del periodo ya pagado.
            </div>
            <Button variant="ghost" onClick={cancelarSusc} loading={cancelarMut.isPending}
                    style={{ color: 'var(--color-error)' }}>Cancelar suscripción</Button>
          </>
        ) : (
          <div style={{ fontSize: '13px', color: 'var(--color-text-2)' }}>
            ✓ Cancelada — el servicio sigue activo hasta {_fmtFecha(cliente.baja_programada_en || cliente.periodo_fin) || 'el fin del periodo'}.
          </div>
        )}
        {msgSusc && (
          <div style={{ marginTop: '10px', fontSize: '12px', fontFamily: 'var(--font-mono)',
            color: msgSusc.ok ? 'var(--color-ok)' : 'var(--color-error)' }}>
            {msgSusc.ok ? '✓' : '✗'} {msgSusc.text}
          </div>
        )}
      </div>
    </div>
  )
}

export default function ClienteDetalle() {
  const { id: clienteId } = useParams()
  const navigate = useNavigate()
  const [tab, setTab] = useState('info')
  const { data: cliente, isLoading, error } = useCliente(clienteId)
  const deleteMutation = useDeleteCliente()

  async function desactivar() {
    const ok = window.confirm(
      `¿Desactivar al cliente "${cliente.nombre}"?\n\n` +
      'Se detiene su scheduler y deja de aparecer en el listado. ' +
      'Sus datos se conservan (borrado suave).'
    )
    if (!ok) return
    try {
      await deleteMutation.mutateAsync(clienteId)
      navigate('/clientes')
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'No se pudo desactivar el cliente.')
    }
  }

  if (isLoading) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header title="Cliente" />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
        Cargando...
      </div>
    </div>
  )

  if (error || !cliente) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header title="Cliente" />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-error)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
        Cliente no encontrado
      </div>
    </div>
  )

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title={cliente.nombre}
        subtitle={`${cliente.cliente_id} · ${cliente.familia_cm}`}
        action={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              size="sm" variant="secondary"
              icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>}
              onClick={() => window.open(`${API_BASE}/scraping/${clienteId}/dashboard`, '_blank')}
            >
              Dashboard
            </Button>
            <Button
              size="sm" variant="secondary"
              icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>}
              onClick={() => navigate(`/scraping/${clienteId}`)}
            >
              Scraping
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={desactivar}
              loading={deleteMutation.isPending}
            >
              Desactivar
            </Button>
          </div>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        {/* Tabs */}
        <div style={{ borderBottom: '1px solid var(--color-border)', marginBottom: '20px', display: 'flex', gap: '0' }}>
          {[
            { key: 'info', label: 'Información' },
            { key: 'plan', label: 'Plan y estado' },
            { key: 'uso', label: 'Uso' },
            { key: 'empresas', label: `Empresas propias (${cliente.empresas_propias?.length ?? 0})` },
            { key: 'competidores', label: `Competidores (${cliente.competidores?.length ?? 0})` },
            { key: 'scheduler', label: 'Scheduler' },
          ].map(t => (
            <TabBtn key={t.key} label={t.label} active={tab === t.key} onClick={() => setTab(t.key)} />
          ))}
        </div>

        <Card>
          {tab === 'info'         && <InfoTab cliente={cliente} />}
          {tab === 'plan'         && <PlanTab cliente={cliente} clienteId={clienteId} />}
          {tab === 'uso'          && <UsoTab clienteId={clienteId} />}
          {tab === 'empresas'     && <EmpresasTab empresas={cliente.empresas_propias ?? []} clienteId={clienteId} />}
          {tab === 'competidores' && <CompetidoresTab competidores={cliente.competidores ?? []} clienteId={clienteId} />}
          {tab === 'scheduler'    && <SchedulerTab scheduler={cliente.scheduler_config} clienteId={clienteId} />}
        </Card>
      </div>
    </div>
  )
}
